import {
  patternWorkPhaseName,
} from "../core/config.js";
import {
  phaseSegments,
} from "../analysis/phase.js";
import {
  clamp01,
} from "../core/math.js";
import { lerp } from "../core/math.js";

export function phaseLabelOptions(label) {
  const compact = {
    Startup: "Start",
    Runup: "Run",
    "Ground Run": "Gnd",
    Takeoff: "TO",
    [patternWorkPhaseName]: "Pattern",
    Cruise: "Cruise",
    Descent: "Desc",
    Landing: "Land",
    Shutdown: "Stop",
  }[label];
  return compact && compact !== label ? [label, compact] : [label];
}

export function phaseRailColor(name, alpha = 1) {
  const rgb = {
    Startup: "148, 163, 184",
    Taxi: "56, 189, 248",
    Runup: "255, 159, 28",
    "Ground Run": "245, 158, 11",
    Takeoff: "255, 73, 61",
    Climb: "98, 206, 77",
    [patternWorkPhaseName]: "45, 212, 191",
    Cruise: "47, 136, 255",
    Descent: "179, 107, 255",
    Landing: "255, 209, 102",
    Shutdown: "148, 163, 184",
  }[name] ?? "150, 166, 183";
  return `rgba(${rgb}, ${alpha})`;
}

export function phaseRailPriority(phase, pixelWidth) {
  const majorBonus = ["Climb", patternWorkPhaseName, "Cruise", "Descent"].includes(phase.name) ? 110 : 0;
  const groundBonus = ["Startup", "Taxi", "Runup", "Ground Run"].includes(phase.name) ? 20 : 0;
  const durationBonus = Math.min(90, (phase.durationSeconds ?? phase.count ?? 0) * 0.04);
  return pixelWidth + majorBonus + groundBonus + durationBonus;
}

export function phaseRailSegments(data, rail) {
  return phaseSegments(data).map((phase, index) => {
    const start = clamp01(phase.start / 100);
    const end = clamp01(phase.end / 100);
    const left = lerp(rail.left, rail.right, start);
    const right = lerp(rail.left, rail.right, end);
    return {
      phase,
      index,
      left,
      right,
      width: right - left,
    };
  }).filter((segment) => segment.width > 0.5);
}

export function isPhaseRailMicroSegment(segment, railWidth) {
  const widthThreshold = Math.max(6, Math.min(12, railWidth * 0.012));
  const duration = segment.phase.durationSeconds;
  const isShortDuration = Number.isFinite(duration) && duration <= 6;
  return segment.width < widthThreshold || (isShortDuration && segment.width < widthThreshold * 1.5);
}

export function nearestFullPhaseSegment(segments, index, direction) {
  for (let cursor = index + direction; cursor >= 0 && cursor < segments.length; cursor += direction) {
    if (!segments[cursor].micro) return segments[cursor];
  }
  return null;
}

export function phaseRailDisplayPlan(segments, rail) {
  const railWidth = Math.max(1, rail.right - rail.left);
  const planned = segments.map((segment) => ({
    ...segment,
    micro: isPhaseRailMicroSegment(segment, railWidth),
  }));
  const slices = planned.map((segment, index) => {
    if (!segment.micro) return { ...segment, paintPhase: segment.phase };
    const previous = nearestFullPhaseSegment(planned, index, -1);
    const next = nearestFullPhaseSegment(planned, index, 1);
    let owner = previous ?? next ?? segment;
    if (previous && next && previous.phase.name === next.phase.name) {
      owner = previous;
    } else if (previous && next && next.width > previous.width * 1.35) {
      owner = next;
    }
    return { ...segment, paintPhase: owner.phase };
  });

  const bands = [];
  slices.forEach((slice) => {
    const last = bands[bands.length - 1];
    if (last && last.phase.name === slice.paintPhase.name) {
      last.right = slice.right;
      last.width = last.right - last.left;
      last.index = Math.min(last.index, slice.index);
      last.microOnly = last.microOnly && slice.micro;
      return;
    }
    bands.push({
      phase: slice.paintPhase,
      index: slice.index,
      left: slice.left,
      right: slice.right,
      width: slice.width,
      microOnly: slice.micro,
    });
  });

  return {
    bands: bands.filter((band) => band.width > 0.5),
    microSegments: planned.filter((segment) => segment.micro),
  };
}

export function phaseRailTransitionZones(microSegments, rail) {
  const zones = [];
  microSegments.forEach((segment) => {
    const center = (segment.left + segment.right) / 2;
    const halfWidth = Math.max(5, Math.min(11, segment.width * 0.9 + 4));
    const zone = {
      left: Math.max(rail.left, center - halfWidth),
      right: Math.min(rail.right, center + halfWidth),
    };
    const previous = zones[zones.length - 1];
    if (previous && zone.left <= previous.right + 4) {
      previous.right = Math.max(previous.right, zone.right);
      return;
    }
    zones.push(zone);
  });
  return zones;
}

export function phaseRailLabelForSegment(ctx, segment) {
  if (segment.width < 26) return null;
  return phaseLabelOptions(segment.phase.name).find((option) => ctx.measureText(option).width + 14 <= segment.width) ?? null;
}

export function rectsOverlap(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}
